import { auth } from "@/lib/firebase";
import { createOrganization } from "@/lib/organization/api";
import {
  applyActionCode,
  signOut as firebaseSignOut,
  GithubAuthProvider,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithPopup,
  User,
} from "firebase/auth";
import { createContext, useEffect, useState } from "react";

type AuthResult = {
  success: boolean;
  signup?: boolean;
  user?: User | null;
  error?: string;
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  gitHubToken: string | null;
  loading: boolean;
  signIn: () => Promise<AuthResult>;
  signUp: () => Promise<AuthResult>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  verifyEmail: (code: string) => Promise<void>;
  sendVerifyEmail: () => Promise<void>;
}
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("https://www.googleapis.com/auth/contacts.readonly");

export const AuthContext = createContext<AuthContextType | null>(null);
const actionCodeSettings = {
  url: "https://dashboard.vibedeploy.com/",
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [gitHubToken] = useState<string | null>(null);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const t = await user.getIdToken();
        setToken(t);
        setUser(user);
        localStorage.setItem("token", t);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signIn = () => {
    return signInWithGithub();
  };

  const forgotPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const signInWithGithub = async (): Promise<AuthResult> => {
    const provider = new GithubAuthProvider();

    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);

      const registerResponse = await createOrganization({
        gitProviderInfo: {
          gitProvider: "github",
          gitAvatarUrl: result.user?.providerData?.[0]["photoURL"]!,
          gitUserId: result.user?.providerData?.[0]["uid"]!,
        },
        name: result.user?.displayName ?? result.user!.email!,
        email: result.user!.email!,
      });

      return {
        success: true,
        user: result.user,
        signup: registerResponse.data?.signup,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = () => {
    signInWithPopup(auth, googleProvider)
      .then(async (result) => {
        // This gives you a Google Access Token. You can use it to access the Google API.
        // const credential = GoogleAuthProvider.credentialFromResult(result);
        const user = result.user;
        const t = await user.getIdToken();
        // The signed-in user info.
        setToken(t);
        setUser(user);
      })
      .catch((_error) => {
        console.error("Authentication with Google failed. Please try again.");
        // Handle Errors here.
        // const errorCode = error.code;
        // const errorMessage = error.message;
        // The email of the user's account used.
        // const email = error.customData.email;
        // The AuthCredential type that was used.
        // const credential = GoogleAuthProvider.credentialFromError(error);
        // ...
      });
  };

  const signUp = async () => {
    return signInWithGithub();
  };

  const verifyEmail = async (code: string) => {
    return applyActionCode(auth, code);
  };

  const sendVerifyEmail = async () => {
    if (user) {
      await sendEmailVerification(user, actionCodeSettings);
    }
  };
  const signOut = async () => {
    // 🛡️ Sentinel: Remove authentication token from local storage to prevent session reuse
    localStorage.removeItem("token");
    await firebaseSignOut(auth);
    window.location.reload();
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    forgotPassword,
    verifyEmail,
    sendVerifyEmail,
    token,
    gitHubToken,
  };
  //@ts-expect-error todo
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
